/**
 * إدارة الوكالة: انضمام العملاء الجدد، والمشاريع والمهام على نسق أسانا.
 *
 * الفكرة سلسلة واحدة: العقد يُفعَّل ← ينطلق انضمامُ العميل تلقائيًّا —
 * مشروعُ تجهيزٍ بمهامه وبريفٌ مشترك — ثم تستمرّ أعمالُه اليومية مشاريعَ
 * ومهامَّ تظهر في ملفّه كما تظهر في لوحة الفريق.
 *
 * ## لماذا نظامُ مهامّ ثانٍ ولوحةُ CRM قائمة؟
 *
 * لوحة الأنشطة متابعاتُ بيعٍ: أعمدةٌ أربعة ثابتة وترتيبٌ محسوب من الموعد
 * والأولوية — قراران موثّقان هناك يخدمان فريق المبيعات. أما تنفيذ أعمال
 * العملاء فيحتاج أقسامًا يسمّيها الفريق، وترتيبًا يدويًّا، ومهامَّ فرعية —
 * نموذجُ أسانا. فرضُ أحدهما على الآخر كان سيُفسدهما معًا.
 */
import type { Labelled } from './common.js';
import type { Priority } from './crm.js';

/* ==========================================================================
   الانضمام
   ========================================================================== */

export const ONBOARDING_STATUSES = ['IN_PROGRESS', 'COMPLETED'] as const;
export type OnboardingStatus = (typeof ONBOARDING_STATUSES)[number];

export const ONBOARDING_STATUS_DEFS: readonly Labelled<OnboardingStatus>[] = [
  { key: 'IN_PROGRESS', label: 'قيد الانضمام', kind: 'info' },
  { key: 'COMPLETED', label: 'اكتمل الانضمام', kind: 'success' },
] as const;

export const ONBOARDING_STATUS_MAP = Object.fromEntries(
  ONBOARDING_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<OnboardingStatus, Labelled<OnboardingStatus>>>;

/**
 * خطوات الانضمام الافتراضية.
 *
 * تُنشأ مهامَّ حقيقيةً في مشروع الانضمام — تُعدَّل وتُحذف ويُضاف إليها
 * كسائر المهام. `offsetDays` يحسب موعد كل خطوة من يوم التفعيل، فيتوزّع
 * التجهيز على أسبوعين بدل أن تستحقّ كل الخطوات في يومٍ واحد.
 */
export const DEFAULT_ONBOARDING_STEPS: readonly {
  title: string;
  note?: string;
  offsetDays: number;
}[] = [
  {
    title: 'مراجعة العقد ونطاق العمل',
    note: 'قراءة بنود العقد والتسليمات المتفق عليها وتوثيق أي ملاحظات.',
    offsetDays: 1,
  },
  {
    title: 'جمع بيانات الوصول والحسابات',
    note: 'حسابات التواصل والإعلانات والاستضافة وكل ما يلزم العمل.',
    offsetDays: 2,
  },
  {
    title: 'كتابة البريف واعتماده من العميل',
    note: 'يُكتب في بطاقة الانضمام: الأهداف والجمهور والنبرة والمحاذير.',
    offsetDays: 4,
  },
  {
    title: 'اجتماع الانطلاق مع العميل',
    note: 'تعارف الفريق، وتثبيت قنوات التواصل ومواعيد التقارير.',
    offsetDays: 5,
  },
  {
    title: 'تجهيز الأدوات والحسابات الداخلية',
    note: 'مجلدات العمل والقوالب وإضافة العميل إلى أدوات الفريق.',
    offsetDays: 7,
  },
  {
    title: 'خطة الشهر الأول وتسليمها',
    note: 'تقويم الأعمال الأولى مبنيًّا على البريف، ويُعتمد من العميل.',
    offsetDays: 10,
  },
] as const;

/* ==========================================================================
   المشاريع والمهام
   ========================================================================== */

/** أقسام المشروع الافتراضية — يعدّلها الفريق بعد الإنشاء كما يشاء. */
export const DEFAULT_PROJECT_SECTIONS = [
  'قائمة المهام',
  'قيد التنفيذ',
  'مراجعة',
  'تسليم',
] as const;

/* ==========================================================================
   قوالب المشاريع — التعريفُ مرّةً، والمشاريعُ نسخٌ منه
   ========================================================================== */

/**
 * قالبُ مشروع.
 *
 * كانت الأقسامُ والحقول تُعرَّف في كل مشروعٍ على حدة، فمن أضاف حقلًا في
 * مشروعٍ لم يره في غيره: عشرةُ مشاريعَ لعملاء التسويق بعشرة تعريفاتٍ
 * متباينة، ومقارنتُها مستحيلةٌ لأن كلًّا يقيس بمسطرته.
 *
 * والقالبُ يُنسخ ولا يُشار إليه: تعديلُه بعد شهورٍ يجب ألّا يغيّر حقولَ
 * مشروعٍ جارٍ فيه بياناتٌ مكتوبة. ومن أراد نشرَ تغييرٍ على القائم فبفعلٍ
 * صريحٍ يقول كم مشروعًا سيمسّ قبل أن يمسّه.
 */
export interface TemplateSectionView {
  id: string;
  name: string;
  order: number;
}

export interface TemplateFieldView {
  id: string;
  name: string;
  kind: AgencyFieldKind;
  options: AgencyFieldOption[];
  order: number;
}

export interface TemplateTaskView {
  id: string;
  title: string;
  notes: string | null;
  offsetDays: number;
  isMilestone: boolean;
  sectionOrder: number;
  order: number;
}

export interface AgencyTemplateView {
  id: string;
  name: string;
  description: string | null;
  color: string;
  icon: string;
  isDefault: boolean;
  archived: boolean;
  portfolioId: string | null;
  portfolioName: string | null;
  projectDescription: string | null;
  sectionCount: number;
  fieldCount: number;
  taskCount: number;
  /** كم مشروعًا أُنشئ منه — القالبُ الذي لا يُستعمل يُقال عنه ذلك. */
  projectCount: number;
  createdAt: string;
}

export interface AgencyTemplateDetail extends AgencyTemplateView {
  sections: TemplateSectionView[];
  fields: TemplateFieldView[];
  tasks: TemplateTaskView[];
}

/**
 * قوالبُ البداية — تُنشأ بنقرةٍ لمن لا قالبَ له.
 *
 * صفحةٌ فارغةٌ تطلب من المستخدم أن يخترع أقسامَ عملِه من عدمٍ أصعبُ من
 * قالبٍ جاهزٍ يحذف منه ما لا يلزمه. وهي أمثلةٌ لا شريعة: تُعدَّل كلُّها
 * بعد الإنشاء.
 */
export const STARTER_TEMPLATES: readonly {
  name: string;
  description: string;
  icon: string;
  color: string;
  sections: readonly string[];
  fields: readonly { name: string; kind: AgencyFieldKind; options?: readonly string[] }[];
  tasks: readonly { title: string; offsetDays: number; sectionOrder: number }[];
}[] = [
  {
    name: 'عملاء التسويق',
    description: 'إدارة حسابات التواصل والإعلانات: خطةٌ شهرية، تصميمٌ ونشرٌ وتقرير.',
    icon: 'send',
    color: '#3672EA',
    sections: ['الخطة والأفكار', 'تصميم وكتابة', 'مراجعة العميل', 'نُشر'],
    fields: [
      { name: 'المنصة', kind: 'SELECT', options: ['فيسبوك', 'إنستجرام', 'تيك توك', 'لينكدإن', 'إعلانات'] },
      { name: 'نوع المحتوى', kind: 'SELECT', options: ['صورة', 'فيديو', 'ريلز', 'مقال', 'إعلان'] },
      { name: 'اعتماد العميل', kind: 'CHECKBOX' },
      { name: 'رابط التسليم', kind: 'TEXT' },
    ],
    tasks: [
      { title: 'خطة المحتوى الشهرية', offsetDays: 3, sectionOrder: 0 },
      { title: 'اعتماد الخطة من العميل', offsetDays: 5, sectionOrder: 2 },
      { title: 'تقرير الأداء الشهري', offsetDays: 30, sectionOrder: 0 },
    ],
  },
  {
    name: 'عملاء العقارات',
    description: 'تسويق المشروعات العقارية: وحداتٌ وحملاتٌ ومعاينات.',
    icon: 'building',
    color: '#51A521',
    sections: ['المشروعات والوحدات', 'إنتاج المواد', 'الحملات', 'المعاينات والمتابعة'],
    fields: [
      { name: 'المشروع العقاري', kind: 'TEXT' },
      { name: 'نوع الوحدة', kind: 'SELECT', options: ['شقة', 'فيلا', 'إداري', 'تجاري', 'أرض'] },
      { name: 'قناة الحملة', kind: 'MULTI_SELECT', options: ['فيسبوك', 'جوجل', 'سناب', 'لوحات'] },
      { name: 'موعد المعاينة', kind: 'DATE' },
    ],
    tasks: [
      { title: 'حصر الوحدات ومواد التسويق', offsetDays: 4, sectionOrder: 0 },
      { title: 'إطلاق الحملة الأولى', offsetDays: 10, sectionOrder: 2 },
    ],
  },
  {
    name: 'الإنتاج الإعلامي',
    description: 'تصويرٌ ومونتاج: من السيناريو إلى النسخة النهائية.',
    icon: 'video',
    color: '#8B5CF6',
    sections: ['ما قبل التصوير', 'التصوير', 'المونتاج', 'التسليم'],
    fields: [
      { name: 'نوع الإنتاج', kind: 'SELECT', options: ['إعلان', 'فيديو تعريفي', 'تغطية', 'فوتوغرافي'] },
      { name: 'موقع التصوير', kind: 'TEXT' },
      { name: 'يوم التصوير', kind: 'DATE' },
      { name: 'المخرج', kind: 'PEOPLE' },
    ],
    tasks: [
      { title: 'السيناريو ولوحة القصة', offsetDays: 5, sectionOrder: 0 },
      { title: 'يوم التصوير', offsetDays: 12, sectionOrder: 1 },
      { title: 'تسليم النسخة النهائية', offsetDays: 25, sectionOrder: 3 },
    ],
  },
  {
    name: 'تطوير المواقع والتطبيقات',
    description: 'مشروعُ برمجةٍ بمراحله: تحليلٌ فتصميمٌ فتطويرٌ فإطلاق.',
    icon: 'gantt',
    color: '#0EA5E9',
    sections: ['التحليل', 'التصميم', 'التطوير', 'المراجعة', 'الإطلاق'],
    fields: [
      { name: 'البيئة', kind: 'SELECT', options: ['تطوير', 'اختبار', 'إنتاج'] },
      { name: 'الأولوية التقنية', kind: 'SELECT', options: ['حرجة', 'عالية', 'عادية'] },
      { name: 'رابط النسخة', kind: 'TEXT' },
    ],
    tasks: [
      { title: 'وثيقة المتطلبات', offsetDays: 5, sectionOrder: 0 },
      { title: 'اعتماد التصميم', offsetDays: 12, sectionOrder: 1 },
      { title: 'الإطلاق', offsetDays: 45, sectionOrder: 4 },
    ],
  },
] as const;

export const MAX_TEMPLATE_SECTIONS = 20;
export const MAX_TEMPLATE_FIELDS = 30;
export const MAX_TEMPLATE_TASKS = 50;

/* ==========================================================================
   نوع المشروع — بوّابة الحوكمة
   ========================================================================== */

export const AGENCY_PROJECT_KINDS = ['CONTRACT', 'TRIAL', 'FREE', 'INTERNAL'] as const;
export type AgencyProjectKind = (typeof AGENCY_PROJECT_KINDS)[number];

export interface AgencyProjectKindDef extends Labelled<AgencyProjectKind> {
  hint: string;
  /** يحتاج عميلًا. */
  needsAccount: boolean;
  /** يحتاج عقدًا ساريًا. */
  needsContract: boolean;
}

export const AGENCY_PROJECT_KIND_DEFS: readonly AgencyProjectKindDef[] = [
  {
    key: 'CONTRACT',
    label: 'تعاقديّ',
    kind: 'success',
    hint: 'عملٌ مدفوع — لا يُنشأ إلا على عقدٍ ساري المفعول',
    needsAccount: true,
    needsContract: true,
  },
  {
    key: 'TRIAL',
    label: 'تجريبيّ',
    kind: 'warning',
    hint: 'عمل اختبارٍ لعميلٍ لم يوقّع بعد',
    needsAccount: true,
    needsContract: false,
  },
  {
    key: 'FREE',
    label: 'مجّانيّ',
    kind: 'info',
    hint: 'عملٌ بلا مقابل لعميلٍ قائم',
    needsAccount: true,
    needsContract: false,
  },
  {
    key: 'INTERNAL',
    label: 'داخليّ',
    kind: 'neutral',
    hint: 'عمل الشركة لنفسها — بلا عميل',
    needsAccount: false,
    needsContract: false,
  },
] as const;

export const AGENCY_PROJECT_KIND_MAP = Object.fromEntries(
  AGENCY_PROJECT_KIND_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<AgencyProjectKind, AgencyProjectKindDef>>;

/** قواعد أتمتة المشروع — محدودةٌ بما يُنفَّذ فعلًا في الخادم. */
export interface ProjectRules {
  /** نقلُ بطاقةٍ إلى هذا القسم يُكملها تلقائيًّا. */
  completeOnSectionId?: string | null;
  /** إكمالُ مهمةٍ ينقلها إلى هذا القسم تلقائيًّا. */
  moveOnCompleteSectionId?: string | null;
}

/* ==========================================================================
   حالة المشروع المعلنة — كأسانا: على المسار، في خطر، متعثّر
   ========================================================================== */

export const PROJECT_HEALTHS = ['ON_TRACK', 'AT_RISK', 'OFF_TRACK'] as const;
export type ProjectHealth = (typeof PROJECT_HEALTHS)[number];

export const PROJECT_HEALTH_DEFS: readonly Labelled<ProjectHealth>[] = [
  { key: 'ON_TRACK', label: 'على المسار', kind: 'success' },
  { key: 'AT_RISK', label: 'في خطر', kind: 'warning' },
  { key: 'OFF_TRACK', label: 'متعثّر', kind: 'danger' },
] as const;

export const PROJECT_HEALTH_MAP = Object.fromEntries(
  PROJECT_HEALTH_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<ProjectHealth, Labelled<ProjectHealth>>>;

/** ألوان المشاريع — لوحةٌ مغلقة فيبقى اللون علامةً لا زينةً عشوائية. */
export const PROJECT_COLORS = [
  '#3672EA',
  '#51A521',
  '#E8A33D',
  '#DC2626',
  '#8B5CF6',
  '#0EA5E9',
  '#EC4899',
  '#6F7784',
] as const;

export const MAX_AGENCY_TASK_TITLE = 200;
export const MAX_AGENCY_NOTES = 4000;
export const MAX_BRIEF_CHARS = 8000;

/* ==========================================================================
   الحقول المخصَّصة — كحقول أسانا، على مستوى المشروع
   ========================================================================== */

export const AGENCY_FIELD_KINDS = [
  'TEXT',
  'NUMBER',
  'SELECT',
  'MULTI_SELECT',
  'DATE',
  'PEOPLE',
  'CHECKBOX',
] as const;
export type AgencyFieldKind = (typeof AGENCY_FIELD_KINDS)[number];

export interface AgencyFieldKindDef extends Labelled<AgencyFieldKind> {
  /** جملة تشرح متى يُستخدم — تظهر في بطاقة الاختيار. */
  hint: string;
  /** هل يحتاج قائمة خيارات. */
  hasOptions: boolean;
}

export const AGENCY_FIELD_KIND_DEFS: readonly AgencyFieldKindDef[] = [
  { key: 'TEXT', label: 'نص', kind: 'neutral', hint: 'ملاحظة قصيرة حرّة', hasOptions: false },
  { key: 'NUMBER', label: 'رقم', kind: 'info', hint: 'ميزانية، عدد، تقدير', hasOptions: false },
  {
    key: 'SELECT',
    label: 'قائمة اختيار',
    kind: 'brand',
    hint: 'خيارٌ واحد بلون — كالحالة أو المنصة',
    hasOptions: true,
  },
  {
    key: 'MULTI_SELECT',
    label: 'اختيار متعدد',
    kind: 'brand',
    hint: 'أكثر من خيار معًا — كالوسوم',
    hasOptions: true,
  },
  { key: 'DATE', label: 'تاريخ', kind: 'success', hint: 'موعدٌ غير موعد الاستحقاق', hasOptions: false },
  { key: 'PEOPLE', label: 'شخص', kind: 'warning', hint: 'عضو فريقٍ — كمراجعٍ أو معتمِد', hasOptions: false },
  { key: 'CHECKBOX', label: 'صندوق تحقّق', kind: 'neutral', hint: 'نعم/لا — كموافقة العميل', hasOptions: false },
] as const;

export const AGENCY_FIELD_KIND_MAP = Object.fromEntries(
  AGENCY_FIELD_KIND_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<AgencyFieldKind, AgencyFieldKindDef>>;

/** خيار قائمة اختيار — معرّفٌ ثابت فتغيير التسمية لا يُفقد قيم المهام. */
export interface AgencyFieldOption {
  id: string;
  label: string;
  color: string;
}

export interface AgencyFieldView {
  id: string;
  name: string;
  kind: AgencyFieldKind;
  options: AgencyFieldOption[];
  order: number;
}

/**
 * قيم حقول مهمة: { fieldId: value }.
 *
 * نصٌّ (نص/تاريخ/اختيار/شخص)، أو رقم، أو نعم/لا، أو مصفوفة معرّفات
 * (الاختيار المتعدد)، أو فارغ.
 */
export type AgencyFieldValue = string | number | boolean | string[] | null;
export type AgencyFieldValues = Record<string, AgencyFieldValue>;

/* ==========================================================================
   الشكل المعاد
   ========================================================================== */

export interface AgencyProjectView {
  id: string;
  name: string;
  description: string | null;
  color: string;
  accountId: string | null;
  accountName: string | null;
  /** صورةُ العميل من ملفّه — الوجهُ نفسُه في كل شاشة، لا حرفٌ أوّل هنا وصورةٌ هناك. */
  accountAvatarUrl: string | null;
  /** مالك المشروع — من يُسأل عنه. */
  ownerId: string | null;
  /** الحالة المعلنة — يضبطها المدير من نظرة المشروع العامة. */
  health: ProjectHealth | null;
  kind: AgencyProjectKind;
  contractId: string | null;
  /** رقم العقد الحاكم — للعرض والوصلة. */
  contractNumber: string | null;
  portfolioId: string | null;
  portfolioName: string | null;
  portfolioColor: string | null;
  goalId: string | null;
  templateId: string | null;
  templateName: string | null;
  archived: boolean;
  /** عدّادا التقدم — من المهام الأمّهات لا الفرعية، فالنسبة تُقرأ صحيحة. */
  taskCount: number;
  doneCount: number;
  /** ما فات موعدُه ولم يكتمل — الرقمُ الذي يقول إن كان المشروع بخير. */
  overdueCount: number;
  /** أقربُ استحقاقٍ مفتوح — «القادم» في البطاقة. */
  nextDueAt: string | null;
  createdAt: string;
}

export interface AgencySectionView {
  id: string;
  name: string;
  order: number;
}

export interface AgencyTaskView {
  id: string;
  projectId: string;
  projectName: string | null;
  projectColor: string | null;
  sectionId: string | null;
  parentId: string | null;
  title: string;
  notes: string | null;
  priority: Priority;
  /** بداية العمل — للخطّ الزمنيّ. */
  startAt: string | null;
  dueAt: string | null;
  completed: boolean;
  completedAt: string | null;
  assigneeId: string | null;
  accountId: string | null;
  accountName: string | null;
  /** خدمة الكتالوج المربوطة ووحداتها — بها يُحتسب استهلاك الباقة. */
  serviceId: string | null;
  serviceName: string | null;
  serviceUnit: string | null;
  units: number;
  /** معلمٌ رئيسيّ — محطة فارقة تُميَّز في اللوحة والقائمة. */
  isMilestone: boolean;
  /** قيم الحقول المخصَّصة { fieldId: value }. */
  fields: AgencyFieldValues;
  order: number;
  subtaskCount: number;
  subtaskDone: number;
  /** عدّادا التعليقات والملفات — يظهران على البطاقة كأسانا. */
  commentCount: number;
  fileCount: number;
  createdAt: string;
}

export interface AgencyProjectDetail {
  id: string;
  name: string;
  description: string | null;
  color: string;
  accountId: string | null;
  accountName: string | null;
  ownerId: string | null;
  health: ProjectHealth | null;
  kind: AgencyProjectKind;
  contractId: string | null;
  contractNumber: string | null;
  portfolioId: string | null;
  goalId: string | null;
  rules: ProjectRules;
  archived: boolean;
  createdAt: string;
  sections: AgencySectionView[];
  /** حقول المشروع المخصَّصة، بترتيبها. */
  fieldDefs: AgencyFieldView[];
  /** المهام الأمّهات فقط — الفرعية تُجلب مع تفصيل المهمة. */
  tasks: AgencyTaskView[];
}

/* ==========================================================================
   باقة العميل (Retainer)
   ========================================================================== */

/** بند استهلاكٍ واحد: خدمة الباقة، مسموحها، وما استُهلك منها هذه الدورة. */
export interface RetainerUsageItem {
  serviceId: string;
  serviceName: string;
  unit: string;
  allowance: number;
  used: number;
  remaining: number;
  /** ما جاوز المسموح — يظهر تحذيرًا لا يُخفى. */
  over: number;
}

/**
 * استهلاك باقة العميل في دورتها الجارية.
 *
 * الاستهلاك محسوبٌ من المهام المكتملة المربوطة بالخدمات — لا عدّاد مخزَّن.
 * والدورة شهريةٌ مرساةً على يوم بدء الاشتراك.
 */
export interface RetainerUsageView {
  id: string;
  accountId: string;
  packageId: string;
  packageName: string;
  startedAt: string;
  cycleStart: string;
  cycleEnd: string;
  items: RetainerUsageItem[];
  /** وحدات أُنجزت هذه الدورة لخدماتٍ خارج الباقة — تُذكر ولا تُبتلع. */
  outsideUnits: number;
}

/** اشتراكٌ في قائمة صفحة الباقات — الاستهلاك نفسه مع اسم العميل. */
export interface RetainerListItem extends RetainerUsageView {
  accountName: string;
}

/**
 * لوحة الوكالة — نظرة اليوم الواحدة.
 *
 * أرقامٌ تجيب أسئلة الصباح: كم عميلًا قيد التجهيز؟ ما المتأخر؟ ما المعالم
 * القادمة؟ ومن تجاوزت باقتُه؟ كلها محسوبةٌ لحظة الطلب لا لقطاتٍ تَقدُم.
 */
export interface AgencyOverview {
  /** عقود سارية — أول محطات الرحلة، من المبيعات. */
  contractsActive: number;
  onboardingActive: number;
  projectsActive: number;
  /** اشتراكات باقاتٍ جارية — آخر المحطات. */
  retainersActive: number;
  tasksOpen: number;
  tasksOverdue: number;
  tasksToday: number;
  /** المعالم القادمة وغير المكتملة — أقربها أولًا. */
  milestones: AgencyTaskView[];
  /** أهم المتأخرات — أقدمها استحقاقًا أولًا. */
  overdue: AgencyTaskView[];
  /** عملاء تجاوزت باقاتهم مسموحها هذه الدورة. */
  retainersOver: RetainerListItem[];

  /* ---- ما يُقرأ رسمًا: الموقفُ كلُّه في نظرة ---- */

  /** عقودٌ سارية بلا مشروعٍ بعد — عملٌ مدفوعٌ لم يبدأ. */
  contractsWaiting: number;
  /** توزيعُ الحالة المعلنة على المشاريع العاملة. */
  health: { onTrack: number; atRisk: number; offTrack: number; unset: number };
  /** المهامُ المفتوحة موزّعةً على آجالها — قوسُ الضغط. */
  load: { overdue: number; today: number; week: number; later: number; noDate: number };
  /** ما أُنجز في كلٍّ من الأسابيع الثمانية الماضية — خطُّ الإيقاع. */
  weekly: { weekStart: string; done: number }[];
  /** المحافظ بتقدّمها — أعمدةُ المقارنة بين خطوط العمل. */
  portfolios: PortfolioView[];
  /** أثقلُ المشاريع مهامًّا مفتوحة — أين يقف العمل الآن. */
  busiestProjects: {
    id: string;
    name: string;
    color: string;
    accountName: string | null;
    accountAvatarUrl: string | null;
    open: number;
    overdue: number;
    done: number;
    total: number;
  }[];
  /** حِملُ الفريق: من عليه ماذا — خمسةُ أثقلَ حِملًا. */
  teamLoad: {
    userId: string;
    name: string;
    avatarUrl: string | null;
    open: number;
    overdue: number;
    doneThisWeek: number;
  }[];
}

/* ==========================================================================
   فريق العمل — من ينفّذ، وكم عليه
   ========================================================================== */

/**
 * عضوُ فريق التنفيذ.
 *
 * يُبنى من الموارد البشرية كفريق المبيعات: كلُّ موظّفٍ عاملٍ يظهر هنا بلا
 * إضافةٍ ثانية — قائمتان لأسماء الناس تعنيان اسمًا في إحداهما لا يعرفه
 * الآخر. ومن لا حسابَ مستخدمٍ له لا تُسنَد إليه مهمة: المهمةُ تحمل معرّف
 * مستخدمٍ لا معرّف موظّف، فيُقال ذلك في صفّه بدل أن يُبحث عنه.
 */
export interface AgencyTeamMember {
  employeeId: string;
  name: string;
  code: string | null;
  jobTitle: string | null;
  department: string | null;
  avatarUrl: string | null;
  /** حسابُ المستخدم — بلا هذا لا يُسنَد إليه شيء. */
  userId: string | null;
  status: string;
  open: number;
  overdue: number;
  dueToday: number;
  doneThisWeek: number;
  /** مشاريعُ عليه فيها مهامٌّ مفتوحة. */
  projects: { id: string; name: string; color: string; open: number }[];
}

/**
 * عقدٌ في شاشة الانضمام — سواءٌ انتظر مشروعَه أو صار له.
 *
 * الشاشةُ قسمان: عقودٌ جديدة تنتظر، وعقودٌ سارية يعمل عليها — والسطرُ
 * الواحدُ يخدم القسمين فلا نوعان لشيءٍ واحد.
 */
export interface ContractRow {
  id: string;
  number: string;
  title: string;
  accountId: string;
  accountName: string;
  accountAvatarUrl: string | null;
  startAt: string;
  endAt: string | null;
  value: number;
  /** مشاريعُ هذا العقد — واحدٌ هو الأصل، وأكثرُ من واحدٍ إرثٌ يُنبَّه عليه. */
  projects: {
    id: string;
    name: string;
    color: string;
    taskCount: number;
    doneCount: number;
    overdueCount: number;
  }[];
  /** بطاقةُ التجهيز إن وُجدت — تقدّمُها وبريفُها. */
  onboarding: {
    id: string;
    status: OnboardingStatus;
    brief: string | null;
    startedAt: string;
    taskCount: number;
    doneCount: number;
  } | null;
}

/** شاشةُ الانضمام كاملةً: ما ينتظر، وما يعمل، وما تكرّر. */
export interface IntakeBoard {
  waiting: ContractRow[];
  running: ContractRow[];
  /** عقودٌ لها أكثرُ من مشروع — إرثُ ما قبل قاعدة «مشروعٌ واحدٌ لكل عقد». */
  duplicated: ContractRow[];
}

/**
 * محفظة — تجميعُ خطوط العمل: كلُّ مشاريع التسويق معًا، وكلُّ العقارات معًا.
 *
 * وعدّاداتُها تأتي محسوبةً من الخادم لا مجموعةً في المتصفّح: كانت الصفحة
 * تجلب كلَّ المشاريع ثمّ تجمع، فمن عنده مئتا مشروعٍ ينتظر تحميلَها كلِّها
 * ليقرأ أربعة أرقام.
 */
export interface PortfolioView {
  id: string;
  name: string;
  description: string | null;
  color: string;
  projectCount: number;
  taskCount: number;
  doneCount: number;
  overdueCount: number;
  /** توزيعُ الحالة المعلنة على مشاريعها — لقراءة الموقف في نظرة. */
  atRisk: number;
  offTrack: number;
  createdAt: string;
}

/** هدفٌ بتقدّمٍ محسوب من مهام مشاريعه — لا نسبةٍ تُكتب باليد. */
export interface GoalView {
  id: string;
  title: string;
  description: string | null;
  ownerId: string | null;
  dueAt: string | null;
  archived: boolean;
  projectCount: number;
  taskCount: number;
  doneCount: number;
}

/**
 * عقدٌ سارٍ ينتظر مشروعه — طابور التشغيل.
 *
 * كل تفعيلٍ يبدأ انضمامًا تلقائيًّا، إلا أن عميلًا له انضمامٌ جارٍ لا
 * يُكرَّر له — فعقدُه الجديد يقف هنا حتى يُنشأ له مشروعُه بيدٍ ونقرة.
 */
export interface IntakeContract {
  id: string;
  number: string;
  title: string;
  accountId: string;
  accountName: string;
  startAt: string;
  value: number;
}

/* ==========================================================================
   معايناتُ القراءة — لمن ينفّذ لا لمن يدير
   ========================================================================== */

/**
 * ملخّص العميل للمنفّذين: من هو وأين يقف عمله — بلا أرقامٍ مالية.
 *
 * الفواتير والمستحقّات في ملفّه الكامل داخل CRM؛ منفّذُ المهمة يحتاج
 * السياق لا الحسابات.
 */
export interface ClientPreview {
  id: string;
  name: string;
  kind: string;
  industry: string | null;
  city: string | null;
  phone: string | null;
  email: string | null;
  ownerId: string | null;
  onboarding: { status: OnboardingStatus; done: number; total: number } | null;
  openTasks: number;
  projects: { id: string; name: string; color: string; kind: AgencyProjectKind }[];
  /** استهلاك الباقة — كمّياتٌ لا أموال. */
  retainer: RetainerUsageView | null;
}

/** ملخّص العقد بلا ماليّات — القيمة والدفعات خلف كلمة سرّ. */
export interface ContractPreview {
  id: string;
  number: string;
  title: string;
  status: string;
  startAt: string;
  endAt: string | null;
  signedAt: string | null;
  signedByName: string | null;
  accountId: string;
  accountName: string;
  /** عناوين البنود وحدها — نصوصُها قد تحمل أرقامًا. */
  clauseTitles: string[];
}

/** الجزء الماليّ من العقد — يُفتح بكلمة سرّ المدير لا بالنقر. */
export interface ContractFinancials {
  value: number;
  currency: string;
  milestones: { title: string; amount: number; dueAt: string | null }[];
}

export interface OnboardingView {
  id: string;
  accountId: string;
  accountName: string;
  contractId: string | null;
  contractNumber: string | null;
  contractTitle: string | null;
  projectId: string | null;
  status: OnboardingStatus;
  brief: string | null;
  startedAt: string;
  completedAt: string | null;
  taskCount: number;
  doneCount: number;
}
